import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Public API
export const publicApi = {
  // Health check
  health: () => api.get('/health'),
  
  // Bookings
  createBooking: (data) => api.post('/bookings', data),
  getAvailableSlots: (date) => api.get(`/bookings/available-slots?date=${date}`),
  
  // Leads
  createLead: (data) => api.post('/leads', data),
  
  // Testimonials
  getTestimonials: () => api.get('/testimonials'),
  
  // Blog
  getBlogPosts: () => api.get('/blog'),
  getBlogPost: (slug) => api.get(`/blog/${slug}`),
  
  // Content
  getContent: (section) => api.get(`/content/${section}`),
  
  // Pricing
  getPricingPackages: () => api.get('/public/pricing-packages'),
  
  // Analytics
  trackEvent: (data) => api.post('/analytics', data),
};

// Admin API
export const adminApi = {
  // Auth
  login: (credentials) => api.post('/admin/login', credentials),
  register: (data) => api.post('/admin/register', data),
  getMe: () => api.get('/admin/me'),
  init: () => api.post('/admin/init'),
  changePassword: (data) => api.post('/admin/change-password', data),
  
  // Bookings
  getBookings: () => api.get('/admin/bookings'),
  updateBooking: (id, status) => api.patch(`/admin/bookings/${id}?status=${status}`),
  
  // Leads
  getLeads: () => api.get('/admin/leads'),
  deleteLead: (id) => api.delete(`/admin/leads/${id}`),
  
  // Testimonials
  getAllTestimonials: () => api.get('/admin/testimonials'),
  createTestimonial: (data) => api.post('/admin/testimonials', data),
  updateTestimonial: (id, data) => api.patch(`/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`),
  
  // Blog
  getAllPosts: () => api.get('/admin/blog'),
  createPost: (data) => api.post('/admin/blog', data),
  updatePost: (id, data) => api.patch(`/admin/blog/${id}`, data),
  deletePost: (id) => api.delete(`/admin/blog/${id}`),
  
  // Content
  updateContent: (section, content) => api.put(`/admin/content/${section}`, content),
  
  // Analytics
  getAnalytics: () => api.get('/admin/analytics/summary'),
  
  // Email Settings
  getEmailSettings: () => api.get('/admin/email-settings'),
  updateEmailSettings: (data) => api.put('/admin/email-settings', data),
  sendTestEmail: (toEmail) => api.post('/admin/email-settings/test', { to_email: toEmail }),
  
  // AI Assistant Settings
  getAISettings: () => api.get('/admin/ai-settings'),
  updateAISettings: (data) => api.put('/admin/ai-settings', data),
  addFAQ: (data) => api.post('/admin/ai-settings/add-faq', data),
  deleteFAQ: (faqId) => api.delete(`/admin/ai-settings/faq/${faqId}`),
  
  // Password Reset
  requestPasswordReset: (email) => api.post('/admin/request-password-reset', { email }),
  verifyResetCode: (data) => api.post('/admin/verify-reset-code', data),
  resetPasswordDirect: (data) => api.post('/admin/reset-password-direct', data),
};

// CMS API
export const cmsApi = {
  // Pages
  getPages: () => api.get('/cms/pages'),
  getPage: (pageId) => api.get(`/cms/pages/${pageId}`),
  createPage: (data) => api.post('/cms/pages', data),
  deletePage: (pageId) => api.delete(`/cms/pages/${pageId}`),
  updatePage: (pageId, data) => api.put(`/cms/pages/${pageId}`, data),
  publishPage: (pageId) => api.post(`/cms/pages/${pageId}/publish`),
  getPageVersions: (pageId) => api.get(`/cms/pages/${pageId}/versions`),
  restorePageVersion: (pageId, versionId) => api.post(`/cms/pages/${pageId}/restore/${versionId}`),
  
  // Content Blocks
  getPageBlocks: (pageId) => api.get(`/cms/pages/${pageId}/blocks`),
  createBlock: (pageId, data) => api.post(`/cms/pages/${pageId}/blocks`, data),
  updateBlock: (blockId, data) => api.put(`/cms/blocks/${blockId}`, data),
  deleteBlock: (blockId) => api.delete(`/cms/blocks/${blockId}`),
  reorderBlocks: (pageId, blockIds) => api.put(`/cms/pages/${pageId}/blocks/reorder`, blockIds),
  
  // Navigation
  getNavigation: () => api.get('/cms/navigation'),
  createNavItem: (data) => api.post('/cms/navigation', data),
  updateNavItem: (navId, data) => api.put(`/cms/navigation/${navId}`, data),
  deleteNavItem: (navId) => api.delete(`/cms/navigation/${navId}`),
  reorderNavigation: (navIds) => api.put('/cms/navigation/reorder', navIds),
  
  // Media
  getMedia: () => api.get('/cms/media'),
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cms/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateMedia: (mediaId, data) => api.put(`/cms/media/${mediaId}`, data),
  deleteMedia: (mediaId) => api.delete(`/cms/media/${mediaId}`),
  
  // Forms
  getForms: () => api.get('/cms/forms'),
  getForm: (formId) => api.get(`/cms/forms/${formId}`),
  createForm: (data) => api.post('/cms/forms', data),
  updateForm: (formId, data) => api.put(`/cms/forms/${formId}`, data),
  deleteForm: (formId) => api.delete(`/cms/forms/${formId}`),
  publishForm: (formId) => api.post(`/cms/forms/${formId}/publish`),
  
  // Form Fields
  getFormFields: (formId) => api.get(`/cms/forms/${formId}/fields`),
  createFormField: (formId, data) => api.post(`/cms/forms/${formId}/fields`, data),
  updateFormField: (fieldId, data) => api.put(`/cms/form-fields/${fieldId}`, data),
  deleteFormField: (fieldId) => api.delete(`/cms/form-fields/${fieldId}`),
  reorderFormFields: (formId, fieldIds) => api.put(`/cms/forms/${formId}/fields/reorder`, fieldIds),
  
  // Form Submissions
  getFormSubmissions: (formId) => api.get(`/cms/forms/${formId}/submissions`),
  updateSubmissionStatus: (submissionId, status) => api.patch(`/cms/submissions/${submissionId}?status=${status}`),
  exportSubmissions: (formId, format) => api.get(`/cms/forms/${formId}/submissions/export?format=${format}`),
  
  // Audit Log
  getAuditLogs: (limit, offset) => api.get(`/cms/audit-logs?limit=${limit || 50}&offset=${offset || 0}`),
  
  // Content Seeder
  seedContent: () => api.post('/cms/seed'),
  
  // Public content
  getPublicPage: (slug) => api.get(`/public/page/${slug}`),
  getPublicNavigation: () => api.get('/public/navigation'),
};

// Legal Agreement API
export const legalApi = {
  // Public
  getAgreement: (agreementId) => api.get(`/legal/agreements/${agreementId}`),
  signAgreement: (data) => api.post('/legal/sign', data),
  
  // Admin
  getAgreements: () => api.get('/admin/legal/agreements'),
  getAgreementAdmin: (agreementId) => api.get(`/admin/legal/agreements/${agreementId}`),
  createAgreement: (data) => api.post('/admin/legal/agreements', data),
  updateAgreement: (agreementId, data) => api.put(`/admin/legal/agreements/${agreementId}`, data),
  activateAgreement: (agreementId) => api.post(`/admin/legal/agreements/${agreementId}/activate`),
  deleteAgreement: (agreementId) => api.delete(`/admin/legal/agreements/${agreementId}`),
  
  // Signatures
  getSignatures: (agreementId) => api.get(`/admin/legal/signatures${agreementId ? `?agreement_id=${agreementId}` : ''}`),
  getSignature: (signatureId) => api.get(`/admin/legal/signatures/${signatureId}`),
  
  // Seed default agreement
  seedDefault: () => api.post('/admin/legal/seed'),
};

// Pricing Packages API
export const pricingApi = {
  // Public
  getPackages: () => api.get('/public/pricing-packages'),
  
  // Admin
  getPackagesAdmin: () => api.get('/admin/pricing-packages'),
  createPackage: (data) => api.post('/admin/pricing-packages', data),
  updatePackage: (id, data) => api.put(`/admin/pricing-packages/${id}`, data),
  deletePackage: (id) => api.delete(`/admin/pricing-packages/${id}`),
  reorderPackages: (order) => api.post('/admin/pricing-packages/reorder', order),
  seedPackages: () => api.post('/admin/pricing-packages/seed'),
};

// Availability/Scheduling API
export const availabilityApi = {
  getSettings: () => api.get('/admin/availability'),
  updateSettings: (data) => api.put('/admin/availability', data),
  getBlockedDates: () => api.get('/admin/blocked-dates'),
  blockDate: (data) => api.post('/admin/blocked-dates', data),
  unblockDate: (id) => api.delete(`/admin/blocked-dates/${id}`),
  getAvailableSlots: (date) => api.get(`/bookings/available-slots-v2?date=${date}`),
};

// Social Media API
export const socialApi = {
  getAccounts: () => api.get('/admin/social-accounts'),
  disconnectAccount: (id) => api.delete(`/admin/social-accounts/${id}`),
  getFacebookAuthUrl: () => api.get('/admin/social/facebook/auth-url'),
  getLinkedInAuthUrl: () => api.get('/admin/social/linkedin/auth-url'),
  getScheduledPosts: () => api.get('/admin/scheduled-posts'),
  createPost: (data) => api.post('/admin/scheduled-posts', data),
  cancelPost: (id) => api.delete(`/admin/scheduled-posts/${id}`),
};

// Email Templates API
export const emailTemplatesApi = {
  getTemplates: () => api.get('/admin/email-templates'),
  getTemplate: (id) => api.get(`/admin/email-templates/${id}`),
  createTemplate: (data) => api.post('/admin/email-templates', data),
  updateTemplate: (id, data) => api.put(`/admin/email-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/admin/email-templates/${id}`),
  seedTemplates: () => api.post('/admin/email-templates/seed'),
};

export default api;
